'use client';

import { AnimatePresence, type HTMLMotionProps } from 'framer-motion';
import { type ReactNode, useEffect, useState } from 'react';

import { MotionDiv } from '@commons/components/Motion';
import Separator from '@commons/components/Separator';
import Text from '@commons/components/Text';
import Flex from '@commons/containers/Flex';

import type { AnimatedHeadlineProps } from '.';
import styles from './AnimatedHeadline.module.scss';

/**
 * Animated Headline block component.
 */

const AnimatedHeadline = ({ content }: AnimatedHeadlineProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const { startWords = [], endWords = [], delay } = content;

  const isEmpty = startWords.length === 0 && endWords.length === 0;

  useEffect(() => {
    if (!isEmpty) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % endWords.length);
      }, delay * 1000);

      return () => clearInterval(interval);
    }
  }, [isEmpty, endWords.length, delay]);

  if (isEmpty) return null;

  const previousStartWord = startWords[currentIndex - 1] || startWords[startWords.length - 1];
  const hasStartWordChanged = startWords[currentIndex] !== previousStartWord;

  const previousEndWord = endWords[currentIndex - 1] || endWords[endWords.length - 1];
  const hasEndWordChanged = endWords[currentIndex] !== previousEndWord;

  return (
    <Flex
      gap="xs"
      width={12}
      align="center"
      justify="space-between"
      className={styles.wrapper}
      direction={{ xs: 'column', lg: 'row' }}
    >
      <AnimatedWord key={`start-${currentIndex}`} enabled={hasStartWordChanged}>
        {startWords[currentIndex]}
      </AnimatedWord>
      <Flex.Item grow shrink>
        <Separator
          color="primary"
          direction={{ xs: 'vertical', lg: 'horizontal' }}
          className={styles.separator}
        />
      </Flex.Item>
      <AnimatedWord key={`end-${currentIndex}`} enabled={hasEndWordChanged}>
        {endWords[currentIndex]}
      </AnimatedWord>
    </Flex>
  );
};

// Type definition for the AnimatedWord component properties.

type AnimatedWordProps = HTMLMotionProps<'div'> & {
  children: ReactNode;
  enabled?: boolean;
};

/**
 * AnimatedWord component.
 *
 * Avoids the need to repeat the same properties for each word.
 */

const AnimatedWord = ({ enabled = true, children, ...props }: AnimatedWordProps) => (
  <AnimatePresence mode="wait">
    <MotionDiv
      layout
      initial={enabled ? { opacity: 0 } : undefined}
      animate={enabled ? { opacity: 1 } : undefined}
      exit={enabled ? { opacity: 0 } : undefined}
      transition={{ duration: 1.5 }}
      {...props}
    >
      <Text as="h2" level="display-1" className={styles.word}>
        {children}
      </Text>
    </MotionDiv>
  </AnimatePresence>
);

export default AnimatedHeadline;
